import { CampaignAuditEntry, CampaignAuditResponse } from 'hooks/queries/useCampaignAudit';
import { getInitials } from 'utils';

const SYSTEM_USER_EMAIL = 'system@curedhealth.com';

/**
 * Check if the email belongs to the system user
 */
export const isSystemUser = (email: string): boolean => email === SYSTEM_USER_EMAIL;

/**
 * Extract user initials from a name or email.
 * Returns 'System' for the system user, otherwise delegates to the shared getInitials utility.
 */
export const getUserInitials = (email: string, name?: string): string => {
    if (isSystemUser(email)) {
        return 'System';
    }
    return getInitials(email, name);
};

/**
 * Format timestamp for activity display
 * - Today: "6:30 pm"
 * - Yesterday: "yesterday"
 * - This year: "02 oct, 10:00 am"
 * - Previous years: "02 sep 2024, 04:30 pm"
 */
export const formatActivityTimestamp = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();

    const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

    const isSameYear = date.getFullYear() === now.getFullYear();

    const timeString = date
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        .toLowerCase();

    if (isToday) {
        return timeString;
    }

    if (isYesterday) {
        return 'yesterday';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toLowerCase();

    if (isSameYear) {
        return `${day} ${month}, ${timeString}`;
    }

    return `${day} ${month} ${date.getFullYear()}, ${timeString}`;
};

/**
 * Format a datetime value for display (used for scheduled times, etc.)
 * e.g., "2026-01-12T16:26:39.567+05:30" -> "for Jan 12, 2026 — 4:26 PM EST"
 */
export const formatScheduledDateTime = (isoString: string): string => {
    const date = new Date(isoString);

    const dateOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    };

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
    };

    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

    return `for ${formattedDate} — ${formattedTime}`;
};

/**
 * Flatten and sort the grouped audit response into a flat array
 * Sorted by created_at descending (newest first)
 */
export const flattenAndSortAuditData = (data: CampaignAuditResponse): CampaignAuditEntry[] => {
    const flatEntries: CampaignAuditEntry[] = [];

    Object.values(data).forEach((entries) => {
        flatEntries.push(...entries);
    });

    // Sort by created_at descending (newest first)
    return flatEntries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};
